import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';
import {
  isGenericContinueAction,
  isRevisionConfirmAction,
  normalizeIntakeAction,
} from './intakeComposerChooserActions.js';
import {
  buildChooserAuthorityFields,
  isBackendQuestionChooserVisible,
  resolveBackendChooserProjection,
  resolveBackendChooserQuestion,
} from './intakeComposerChooserAuthority.js';
import {
  buildCurrentQuestionOptions,
  hasConcreteQuestionOption,
  resolveQuestionTitle,
} from './intakeComposerChooserOptions.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

function resolveObject(value = null) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function normalizeText(value = '') {
  return String(value || '').trim();
}

function isRequirementIntakePayload(payload = {}) {
  const modeKey = normalizeText(payload.mode_key || payload.mode || payload.current_mode)
    .toLowerCase()
    .replace(/[-\s]+/g, '_');
  return modeKey === 'requirement_intake';
}

function resolveLatestPrimaryFlowPayload(timelineItems = []) {
  const item = [...(Array.isArray(timelineItems) ? timelineItems : [])]
    .reverse()
    .find((entry) => normalizeText(entry?.type) === PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE);
  const payload = resolveObject(item?.payload);
  return payload ? { payload, roundKey: normalizeText(item?.roundKey || item?.round_key) } : null;
}

function isQuestionInactive(currentQuestion = null) {
  const status = normalizeText(
    currentQuestion?.question_status
    || currentQuestion?.status
    || '',
  ).toLowerCase();
  return ['answered', 'stale', 'dismissed', 'completed', 'applied'].includes(status);
}

function buildCurrentQuestionView(currentQuestion = {}, title = '') {
  return {
    fieldKey: normalizeText(firstTruthy(() => (currentQuestion.field_key), () => ('intake_context'))),
    questionKey: normalizeText(currentQuestion.question_key),
    targetInformationGap: normalizeText(currentQuestion.target_information_gap),
    questionId: normalizeText(currentQuestion.question_id),
    questionRevision: normalizeText(firstTruthy(() => (currentQuestion.question_revision), () => (currentQuestion.revision))),
    questionStatus: normalizeText(firstTruthy(() => (currentQuestion.question_status), () => (currentQuestion.status), () => ('active'))),
    questionKind: normalizeText(firstTruthy(() => (currentQuestion.question_kind), () => ('followup'))),
    branchNodeKey: normalizeText(currentQuestion.branch_node_key),
    questionText: title,
    stepIndex: Number.isFinite(Number(currentQuestion.step_index)) ? Number(currentQuestion.step_index) : 0,
    stepTotal: Number.isFinite(Number(currentQuestion.step_total)) ? Number(currentQuestion.step_total) : 0,
  };
}

function resolveChooserKey({ roundKey, currentQuestion, title, optionCount, modeState }) {
  return `intake-chooser:${[
    normalizeText(roundKey) || 'latest',
    normalizeText(currentQuestion?.question_revision || currentQuestion?.revision),
    normalizeText(currentQuestion?.field_key),
    title,
    String(optionCount),
    normalizeText(modeState).toLowerCase(),
  ].filter(Boolean).join(':')}`;
}

function buildQuestionChooser({ payload, roundKey }) {
  const chooserProjection = resolveBackendChooserProjection(payload);
  const currentQuestion = resolveBackendChooserQuestion(payload);
  if (firstTruthy(() => (!currentQuestion), () => (isQuestionInactive(currentQuestion)))) {
    return null;
  }
  const actions = (Array.isArray(payload.next_actions) ? payload.next_actions : [])
    .map((action, index) => normalizeIntakeAction(action, index))
    .filter((action) => !isRevisionConfirmAction(action));
  const fallbackAction = firstTruthy(() => (actions.find((action) => isGenericContinueAction(action))), () => (null));
  const options = buildCurrentQuestionOptions({ currentQuestion, fallbackAction });
  if (firstTruthy(() => (options.length === 0), () => (!hasConcreteQuestionOption(options)))) {
    return null;
  }

  const title = resolveQuestionTitle(currentQuestion);
  const chooserKey = resolveChooserKey({
    roundKey,
    currentQuestion,
    title,
    optionCount: options.length,
    modeState: payload.mode_state,
  });
  const resumeRunId = normalizeText(firstTruthy(() => (chooserProjection.resume_run_id), () => (chooserProjection.resumeRunId)));
  const interactionId = normalizeText(firstTruthy(() => (chooserProjection.interaction_id), () => (chooserProjection.interactionId)));
  return {
    key: chooserKey,
    ...buildChooserAuthorityFields(payload, chooserKey),
    interactionId,
    interaction_id: interactionId,
    resumeRunId,
    resume_run_id: resumeRunId,
    runId: resumeRunId,
    run_id: resumeRunId,
    title,
    questionRevision: normalizeText(firstTruthy(() => (Object(currentQuestion).question_revision), () => (Object(currentQuestion).revision))),
    pages: [{ options }],
    currentPage: 1,
    totalPages: 1,
    recommendedOptionKey: normalizeText(Object(options[0]).key),
    confirmOnSelect: true,
    footer: {
      dismissLabel: '取消',
      submitLabel: '应用',
    },
    currentQuestion: buildCurrentQuestionView(currentQuestion, title),
  };
}

export function selectIntakeComposerChooser({ timelineItems } = {}) {
  const latest = resolveLatestPrimaryFlowPayload(timelineItems);
  if (!latest || !isRequirementIntakePayload(latest.payload) || !isBackendQuestionChooserVisible(latest.payload)) {
    return null;
  }
  return buildQuestionChooser(latest);
}

export default selectIntakeComposerChooser;
